import React, { useEffect, useState, useId } from "react";
import api from "../../api/api";
import moment from "moment";
import { BarChart, Bar, Rectangle, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";
import { toast } from "react-toastify";
import * as EXCELJS from "exceljs";
import { formatDateCellsExcelJS } from "../../utils/excelDateFormatter";
import { MonthRangeSelector } from "../../components/MonthRangeSelector";

const numero = new Intl.NumberFormat("it-IT");
const COLORI = { Z: "#2563EB", I: "#16A66A", B: "#7C5CE7", G: "#F59E0B", non_classificati: "#94A3B8" };
const nomeTipo = tipo => tipo === "non_classificati" ? "Non classificati" : tipo;
const formatoMeseItaliano = new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric", timeZone: "UTC" });
const nomeMese = periodo => formatoMeseItaliano.format(new Date(`${periodo}-01T00:00:00Z`));
const PRESET = [["mese_corrente", "Mese corrente"], ["mese_precedente", "Mese precedente"], ["ultimo_trimestre", "Ultimo trimestre"], ["ultimi_6", "Ultimi 6 mesi"], ["ultimi_12", "Ultimi 12 mesi"], ["ytd", "Anno in corso"]];

export const Globale = () => {
  const chartId = useId().replace(/:/g, "");
  const [dataA, setDataA] = useState(moment().format("YYYY-MM"));
  const [dataDa, setDataDa] = useState(moment().subtract(6, "months").format("YYYY-MM"));
  const [preset, setPreset] = useState("personalizzato");
  const [parcellazione, setParcellazione] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true); setParcellazione([]);
    api.get("/parcellazione/statistiche/globale", { params: { da: dataDa, a: dataA } })
      .then(({ data }) => { if (active) setParcellazione(data); })
      .catch(() => { if (active) toast.error("Errore durante il caricamento delle statistiche globali"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [dataDa, dataA]);
  const scegliPeriodo = (da, a) => {
    const estremi = [da, a].sort(); setDataDa(estremi[0]); setDataA(estremi[1]);
  };
  const applicaPreset = value => {
    setPreset(value);
    let da = moment().subtract(1, "month"), a = da.clone();
    if (value === "mese_corrente") da = a = moment();
    if (value === "ultimo_trimestre") { da = moment().subtract(1, "quarter").startOf("quarter"); a = da.clone().endOf("quarter"); }
    if (value === "ultimi_6") da.subtract(5, "months");
    if (value === "ultimi_12") da.subtract(11, "months");
    if (value === "ytd") da.startOf("year");
    scegliPeriodo(da.format("YYYY-MM"), a.format("YYYY-MM"));
  };
  const dati = parcellazione.map(r => ({ ...r, totale_cedolini: Number(r.totale_cedolini || 0),
    ...Object.fromEntries(Object.keys(COLORI).map(tipo => [tipo, Number(r[tipo] || 0)])) }));
  const tipi = ["Z", "I", "B", "G", ...(dati.some(r => r.non_classificati !== 0) ? ["non_classificati"] : [])];
  const formaColonna = (props, tipo, index) => {
    const value = Number(props.payload?.[tipo] || 0);
    if (!value || !props.height || !props.width) return <g/>;
    const scale = props.height / value;
    const sopra = tipi.slice(index + 1).reduce((sum, key) => sum + Number(props.payload[key] || 0), 0);
    const sotto = tipi.slice(0, index).reduce((sum, key) => sum + Number(props.payload[key] || 0), 0);
    const top = props.y - sopra * scale;
    const height = props.height + (sopra + sotto) * scale;
    const radius = Math.min(16, props.width / 5, height / 2);
    const clipId = `globale-${chartId}-${props.index}-${tipo}`;
    // Clip the entire stack, not the tiny last segment (e.g. type G).
    return <g><defs><clipPath id={clipId}><rect x={props.x} y={top} width={props.width} height={height} rx={radius} ry={radius}/></clipPath></defs><g clipPath={`url(#${clipId})`}><Rectangle {...props} radius={0}/></g></g>;
  };
  const stampaReport = async (tipo) => {
    let parc;
    if (tipo === "1") {
      parc = await api.get(`/parcellazione/statistiche/globale/report`, {
        params: { da: dataDa, a: dataA, tipo: tipo },
      });
    } else {
      parc = await api.get(`/parcellazione/statistiche/globale/report`, {
        params: { da: dataDa, a: dataA, tipo: tipo },
      });
    }

    if (!parc.data.length) { toast.info('Nessun dato da esportare'); return; }
    let data = parc.data;
    let colonne = Object.keys(data[0]).map((c) => c);
    // Extract Data (create a workbook object from the table)
    var workbook = new EXCELJS.Workbook();

    // Process Data (add a new row)
    var worksheet = workbook.addWorksheet("Data");
    //worksheet.pageSetup.printArea = "A1:G20";
    worksheet.addRow(colonne);

    var output = data.map((obj) => Object.keys(obj).map((key) => obj[key]));
    worksheet.addRows(output);
    formatDateCellsExcelJS(worksheet);
    // Keep the original report layout and formats; only fit the columns and style the header.
    worksheet.columns.forEach(column => {
      let width = 10;
      const headerText = worksheet.getCell(1, column.number).text;
      let hasDate = /(^|_)data(_|$)|date/i.test(headerText);
      column.eachCell((cell, rowNumber) => {
        if (rowNumber === 1) return;
        if (cell.value instanceof Date) hasDate = true;
        width = Math.max(width, (cell.value instanceof Date ? 10 : cell.text.length) + 3);
      });
      column.width = hasDate ? Math.max(16, headerText.length + 3) : Math.min(255, Math.max(width, headerText.length + 3));
    });
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      row.eachCell(cell => {
        cell.font = { name: "Calibri", size: 11 };
        cell.alignment = { vertical: "middle", wrapText: false };
      });
      row.height = 20;
    });
    worksheet.getRow(1).height = 26;
    worksheet.getRow(1).eachCell(cell => {
      cell.font = { name: "Calibri", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { vertical: "middle", wrapText: false };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2563EB" } };
    });
    output.forEach((row, rowIndex) => {
      row.forEach((value, colIndex) => {
        // Ottieni l'oggetto cella
        var cell = worksheet.getCell(rowIndex + 2, colIndex + 1); // Considera che stiamo partendo dalla riga 2 (dopo l'intestazione)

        // Definisci i criteri per il colore
        // Rosso: azienda attiva ma 0 cedolini (problema)
        if (value === 0 || value === "0") {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFF0000" }, // Rosso
          };
        // Grigio: azienda chiusa o nessun dato (-)
        } else if (value === "-") {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF808080" }, // Grigio
          };
        }
      });
    });
    const excelBlob = await workbook.xlsx.writeBuffer();
    const excelUrl = URL.createObjectURL(
      new Blob([excelBlob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );

    const link = document.createElement("a");
    link.href = excelUrl;
    link.download = "report.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(excelUrl), 1000);
  };

  const esporta = async () => {
    setExporting(true);
    try { await stampaReport("1"); } catch (error) { toast.error("Errore durante l’esportazione del report"); }
    finally { setExporting(false); }
  };
  return <div className="el-container h-fit space-y-4">
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-5 py-4">
        <div><h2 className="text-lg font-bold text-slate-800">Statistiche globali</h2><p className="text-sm text-slate-500">Cedolini di allineamento nel periodo selezionato</p></div>
        <button type="button" onClick={esporta} disabled={loading || exporting} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">{exporting ? "Esportazione…" : "Report 1"}</button>
      </div>
      <div className="space-y-3 p-4">
        <MonthRangeSelector da={dataDa} a={dataA} onChange={(da, a) => { setPreset("personalizzato"); scegliPeriodo(da, a); }}/>
        <div className="flex flex-wrap gap-2">{PRESET.map(([value, label]) => <button type="button" key={value} onClick={() => applicaPreset(value)} className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition ${preset === value ? "border-blue-600 bg-blue-600 text-white" : "border-blue-200 bg-white text-slate-700 hover:bg-blue-50"}`}>{label}</button>)}</div>
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-b-2xl border-t border-blue-100 bg-blue-50 px-4 py-3"><span className="text-xs font-semibold uppercase tracking-wide text-blue-600">Periodo analizzato</span><span className="font-semibold capitalize text-slate-800">{dataDa === dataA ? nomeMese(dataA) : `${nomeMese(dataDa)} – ${nomeMese(dataA)}`}</span></div>
    </section>
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-lg font-bold text-slate-800">Andamento mensile dei cedolini</h3>
      <p className="text-sm text-slate-500">Totali di allineamento, suddivisi per tipologia Z, I, B e G</p>
      {loading ? <div className="p-16 text-center text-slate-500">Caricamento…</div> : !dati.length ? <div className="p-16 text-center text-slate-500">Nessun dato nel periodo selezionato.</div> : <div className="mt-5 overflow-x-auto">
        <div style={{ width: `min(100%, ${Math.max(680, dati.length * 150)}px)`, minWidth: Math.max(540, dati.length * 90), height: 440 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dati} margin={{ top: 32, right: 24, left: 18, bottom: 12 }} barCategoryGap="10%">
              <CartesianGrid vertical={false} stroke="#E7EDF5" strokeDasharray="3 4"/>
              <XAxis dataKey="periodo" tickFormatter={v => moment(`${v}-01`).format("MM/YYYY")} axisLine={false} tickLine={false} tick={{ fill: "#475569", fontSize: 12 }}/>
              <YAxis axisLine={false} tickLine={false} tickFormatter={v => numero.format(v)} tick={{ fill: "#64748B", fontSize: 11 }} domain={[0, max => Math.ceil(max * 1.12)]}/>
              <Tooltip cursor={{ fill: "#EFF6FF" }} content={({ active, payload }) => {
                const r = payload?.[0]?.payload;
                if (!active || !r) return null;
                return <div className="min-w-48 rounded-xl border border-blue-100 bg-white p-3 shadow-lg"><div className="mb-2 font-semibold capitalize text-slate-800">{nomeMese(r.periodo)}</div>{tipi.map(tipo => <div key={tipo} className="flex justify-between gap-6" style={{ color: COLORI[tipo] }}><span>{nomeTipo(tipo)}</span><span className="font-semibold">{numero.format(r[tipo])}</span></div>)}<div className="mt-2 flex justify-between gap-6 border-t border-slate-100 pt-2 font-bold"><span>Totale cedolini</span><span>{numero.format(r.totale_cedolini)}</span></div></div>;
              }}/>
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 10 }}/>
              {tipi.map((tipo, index) => <Bar key={tipo} dataKey={tipo} name={nomeTipo(tipo)} stackId="cedolini" fill={COLORI[tipo]} maxBarSize={180} shape={props => formaColonna(props, tipo, index)} activeBar={props => formaColonna(props, tipo, index)}>
                {index === tipi.length - 1 && <LabelList dataKey="totale_cedolini" position="top" formatter={value => numero.format(value)} fill="#1E293B" fontSize={13} fontWeight={700}/>}
              </Bar>)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>}
    </section>
  </div>;
};
